import type { JSX } from "react";
import { useState } from "react";
import { IconChevron } from "../icons";
import type { ToolCall, ToolKind } from "@shared/types";

/** Short icon-badge text per tool kind. */
function kindIcon(kind: ToolKind): string {
  return kind === "kb" ? "KB" : kind === "web" ? "WEB" : kind === "code" ? "码" : "T";
}

interface ToolCallCardProps {
  call: ToolCall;
}

/**
 * Tool-call trace card. Collapsed by default; click to expand. The
 * expanded view per interaction-map.md state 01 will show query, top-k
 * passages, latency timeline — wired up in phase E when retrieval is
 * real. For now expand shows the detail string.
 */
export function ToolCallCard({ call }: ToolCallCardProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const icon = kindIcon(call.kind);
  const iconClass = call.status === "low_confidence" ? "tool-icon warn" : "tool-icon";

  const meta = (() => {
    if (call.detail) return call.detail;
    const parts: string[] = [];
    if (call.query) parts.push(`"${call.query}"`);
    if (call.resultCount !== undefined) parts.push(`${call.resultCount} 个结果`);
    if (call.topScore !== undefined) parts.push(`top ${call.topScore.toFixed(2)}`);
    if (call.latencyMs !== undefined) parts.push(`${(call.latencyMs / 1000).toFixed(1)}s`);
    return parts.join(" · ");
  })();

  const stateClass =
    call.status === "low_confidence" ? "tool-trace low" : call.status === "error" ? "tool-trace err" : "tool-trace";

  return (
    <>
      {/* A real <button>, not a clickable <div>: keyboard-operable (Enter/Space)
          and announced as an expandable control (codex 2026-06-02 §1). aria-expanded
          reflects the collapsed/expanded trace state. */}
      <button
        type="button"
        className={stateClass}
        aria-expanded={open}
        {...(call.foldBody !== undefined ? { "aria-controls": `tool-fold-${call.id}` } : {})}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={iconClass}>{icon}</span>
        <span className="tool-label">{call.label}</span>
        <span className="tool-meta">{meta}</span>
        {call.status === "low_confidence" && <span className="tool-badge">相关度低</span>}
        <span className="tool-chevron" style={{ transform: open ? "rotate(180deg)" : "" }}>
          <IconChevron size={14} />
        </span>
      </button>
      {/* Folded body (agent-profile §07 consult workbar「查看过程」): server-
          composed text revealed on expand. Absent on every other card. */}
      {call.foldBody !== undefined && open && (
        <div id={`tool-fold-${call.id}`} className="tool-fold-body">
          {call.foldBody}
        </div>
      )}
    </>
  );
}

/**
 * A collapsed group of same-label tool calls (e.g. `read_source` invoked many
 * times for a code question). One header card "读源码 ×8 · 总时长" expands to the
 * individual trace cards — so a code answer is not preceded by a wall of cards.
 * A group of one renders as a plain {@link ToolCallCard}.
 */
export function ToolCallGroup({ calls }: { calls: ToolCall[] }): JSX.Element {
  const [open, setOpen] = useState(false);
  const head = calls[0];
  if (calls.length <= 1 || head === undefined) {
    return head !== undefined ? <ToolCallCard call={head} /> : <></>;
  }
  const icon = kindIcon(head.kind);
  const totalS = (calls.reduce((s, c) => s + (c.latencyMs ?? 0), 0) / 1000).toFixed(1);
  const anyLow = calls.some((c) => c.status === "low_confidence");
  return (
    <>
      <button
        type="button"
        className={anyLow ? "tool-trace low" : "tool-trace"}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={anyLow ? "tool-icon warn" : "tool-icon"}>{icon}</span>
        <span className="tool-label">{head.label}</span>
        <span className="tool-meta">×{calls.length} · {totalS}s</span>
        {anyLow && <span className="tool-badge">相关度低</span>}
        <span className="tool-chevron" style={{ transform: open ? "rotate(180deg)" : "" }}>
          <IconChevron size={14} />
        </span>
      </button>
      {open && (
        <div style={{ marginLeft: 16, marginTop: 4, display: "flex", flexDirection: "column", gap: 4 }}>
          {calls.map((c) => (
            <ToolCallCard key={c.id} call={c} />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Neutral umbrella over a run of code-Q&A tool calls (atlas_map / read_source /
 * grep_source). The UI shows ONE static "代码检索 · N 步" card — the highest-level
 * neutral term only. The per-tool sub-steps (代码地图 / 搜代码 / 读源码) are
 * deliberately NOT exposed: they name internal retrieval primitives, so the card
 * is non-expandable (no chevron, no click). Internal-only callers see this;
 * customers never get code tools at all.
 */
export function CodeToolGroup({ groups }: { groups: ToolCall[][] }): JSX.Element {
  const all = groups.flat();
  const totalS = (all.reduce((s, c) => s + (c.latencyMs ?? 0), 0) / 1000).toFixed(1);
  return (
    <div className="tool-trace tool-trace--static">
      <span className="tool-icon">码</span>
      <span className="tool-label">代码检索</span>
      <span className="tool-meta">{all.length} 步 · {totalS}s</span>
    </div>
  );
}
